import Button from "@/components/ui/button";
import { track } from "@/features/analytics";
import { useBulkDeleteMediaLibFilesMutation } from "@api/media-library";
import Spinner from "@components/ui/spinner";
import {
  MediaLibDialogBase,
  useMediaLibDialogStoreShallow,
} from "@editor/modules/media-lib";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { toast } from "react-toastify";

const DIALOG_ID = "media-lib-dailog-bulk-files-delete";

interface IProps {
  bulkDeleteFiles: (ids: string[]) => void;
}

export const MediaLibDialogBulkFilesDeleteDialog = ({
  bulkDeleteFiles,
}: IProps) => {
  const dispatch = useAppDispatch();

  const { items, setSelectedItems } = useMediaLibDialogStoreShallow((s) => ({
    items: s.selectedItems,
    setSelectedItems: s.setSelectedItems,
  }));

  const [deleteFiles, { isLoading: isSaving }] =
    useBulkDeleteMediaLibFilesMutation();

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isSaving) return;

    try {
      const ids = items
        .filter((el) => el.mediaFrom === "my-library")
        .map((el) => el.id);
      if (ids.length === 0) return;

      await deleteFiles({
        ids,
      }).unwrap();
      bulkDeleteFiles(ids);
      setSelectedItems([]);
    } catch (e) {
      track("error archiving media files", {
        items,
        error: e,
      });
      toast.error("Error archiving files");
    }

    handleClose();
  };

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header title="Delete files" onClose={handleClose} />
      <MediaLibDialogBase.Content>
        <p className="tw-text-[13px]">
          Are you sure that you want to delete selected files?
        </p>
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          className={cn(isSaving && "tw-cursor-not-allowed")}
        >
          {isSaving ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Deleting...
            </>
          ) : (
            "Yes, delete"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibDialogBulkFilesDeleteDialog.dialogId = DIALOG_ID;
